import React, { FC, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import BurgerMenuIcon from '../../assets/icons/burger-menu-icon';
import { CloseIcon, LogoutIcon, MessageIcon, ProfileIcon } from '../../assets/icons/index';
import { useHeaderContext } from '../../context/header-context';
import { useNotifications } from '../../context/notifications-context';
import { sideBarContext } from '../../context/side-bar-context';
import { useModalState, User, useScreenSize } from '../../hooks/index';
import { pageTitleTranslation } from '../../translations/page-translation';
import { replaceIdWithPath, TermsRoute } from '../../types/page.types';
import { AppFormattedMessage } from '../app-formatted-message/index';
import Button from '../button/index';
import CompanySelect from '../company-select/index';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '../popover/index';
import { Tooltip } from '../tooltip/index';

interface HeaderProps {
  pageName?: string;
}

const Header: FC<HeaderProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isShownCompanySelect } = useHeaderContext();

  const { closeSideBar, isOpen, openSideBar } = useContext(sideBarContext);
  const { isMobile } = useScreenSize();

  const { user } = User.useUser();
  const { logoutUser } = User.useLogout();

  const handleLogout = useCallback(() => {
    logoutUser();
  }, [logoutUser]);
  const { unreadNotification } = useNotifications();
  const { setParamValue: openActivityCenter } = useModalState('activity-bar');

  const landingBaseUrl = process.env.REACT_APP_LANDING_URL;

  return (
    <div className="flex w-full items-center justify-between rounded-b-[12px] bg-white px-4 py-3 font-inter shadow-xs lg:rounded-b-[24px]">
      <div className="flex w-full items-center gap-2">
        <Button
          className="size-5 lg:hidden"
          onClick={isOpen ? closeSideBar : openSideBar}
          styleType="NONE"
        >
          {isOpen ? (
            <CloseIcon className="size-[10px]" />
          ) : (
            <BurgerMenuIcon height={20} width={20} />
          )}
        </Button>
        <div className="text-base font-[550] text-gray-700 lg:text-xl">
          <AppFormattedMessage id={pageTitleTranslation[replaceIdWithPath(location.pathname)]} />
        </div>
      </div>

      <div className="flex w-1/2 items-center justify-end gap-4">
        {!(isOpen && isMobile) && isShownCompanySelect && <CompanySelect isMobile={isMobile} />}

        <div
          className="relative h-fit w-fit cursor-pointer lg:hidden"
          onClick={() => openActivityCenter('true')}
        >
          <MessageIcon />
          {!!unreadNotification.total && (
            <span
              className="absolute -right-1 -top-1 flex size-4 shrink-0 items-center justify-center rounded-full bg-fireside-600 text-white"
              style={{
                fontSize: '9px',
                fontWeight: '600',
                lineHeight: '11.2px',
                textAlign: 'center',
              }}
            >
              {unreadNotification.total}
            </span>
          )}
        </div>

        <Popover>
          <PopoverTrigger className="flex h-[32px] w-[32px] min-w-[32px] items-center justify-center overflow-hidden rounded-[32px] bg-brand-100 ring-brand-700 data-[state=open]:ring-2 max-lg:hidden">
            <div className="flex h-full w-full items-center justify-center rounded-full">
              {user?.fullName[0]}
            </div>
          </PopoverTrigger>
          <PopoverContent className="flex w-fit flex-col gap-[6px] rounded p-2 text-sm font-normal shadow-2xl">
            <div className="min-w-[190px] divide-y-[1px] divide-gray-200">
              <div className="flex w-full flex-col items-start gap-1 p-[6px]">
                <Tooltip childrenWrapperClassName="max-w-[150px]" content={user?.fullName}>
                  <span className="truncate text-sm font-[450] text-gray-800">
                    {user?.fullName}
                  </span>
                </Tooltip>
                <Tooltip childrenWrapperClassName="max-w-[150px]" content={user?.email}>
                  <span className="truncate text-label-sm font-medium text-gray-500">
                    {user?.email}
                  </span>
                </Tooltip>
              </div>
              <PopoverClose className="w-full">
                <div
                  className="flex h-[48px] items-center gap-[6px] border-b border-gray-200 px-2 py-[6px]"
                  onClick={() => navigate('/profile')}
                >
                  <ProfileIcon />
                  <span className="text-sm font-[450] text-gray-800">
                    <AppFormattedMessage id="MY_PROFILE" />
                  </span>
                </div>
                <div
                  className="flex h-[48px] items-center gap-[6px] border-b border-gray-200 px-2 py-[6px]"
                  onClick={handleLogout}
                >
                  <LogoutIcon />
                  <span className="text-sm font-[450] text-fireside-600">
                    <AppFormattedMessage id="LOG_OUT" />
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-center gap-2 px-2 py-[6px] text-label-sm text-gray-500">
                  <a href={`${landingBaseUrl}${TermsRoute.PRIVACY_POLICY}`} target="_blank">
                    <AppFormattedMessage id="PRIVACY_POLICY" />
                  </a>
                  <div className="h-1 w-1 rounded-full bg-[#D9D9D9]" />
                  <a href={`${landingBaseUrl}${TermsRoute.TERMS_OF_SERVICE}`} target="_blank">
                    <AppFormattedMessage id="TERMS_OF_SERVICE" />
                  </a>
                </div>
              </PopoverClose>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default Header;
